/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback } from "react";

import {
	useLazyGetListQuery,
	useUpdateListColumnsMutation,
} from "@/pages/Private/redux/list/list.api";

import { ColumnConfig } from "../../../../List/schema/columnConfig";

import { findSnippetColumnByName } from "../utils/snippetTokens";

export type PersistResult = { ok: true; updatedColumn: ColumnConfig | null } | { ok: false };

/**
 * Persists an AI snippet column via the list PATCH endpoint and resolves the
 * (possibly new) column id by name — with a list refetch as fallback, because
 * the PATCH response can carry a `columns` snapshot taken before the column we
 * just created. The fallback GET also repopulates the list slice (inbox), which
 * matters for parents that read `listData` from there.
 */
export const useSnippetPersistence = (listId?: number) => {
	const [update, { isLoading: isSaving }] = useUpdateListColumnsMutation();
	const [fetchList] = useLazyGetListQuery();

	const persist = useCallback(
		async (payload: any): Promise<PersistResult> => {
			if (listId == null) {
				return { ok: false };
			}

			try {
				const result: any = await update(payload).unwrap();

				// The mutation returns the updated list with all columns; find the
				// snippet that matches our name to get its (possibly new) id.
				let updatedColumn = findSnippetColumnByName(result?.columns, payload.customColumnName);

				// When the column is missing from the PATCH snapshot, refetch the
				// list — a fresh GET always reflects the current columns — and
				// resolve the id by name. This is what keeps the inserted token from
				// falling back to a non-id like `new`.
				if (!updatedColumn) {
					try {
						const fresh: any = await fetchList(listId, false).unwrap();

						updatedColumn = findSnippetColumnByName(fresh?.columns, payload.customColumnName);
					} catch (e) {
						console.error("Failed to refetch list to resolve AI snippet column id", e);
					}
				} else {
					// The id came straight from the PATCH response, so no GET ran yet.
					// Fire one (without blocking the caller) so the list slice —
					// including `inbox[i].list.columns` in the messages module — picks
					// up the new column.
					fetchList(listId, false);
				}

				return { ok: true, updatedColumn: updatedColumn ?? null };
			} catch (e) {
				console.error("Failed to save AI snippet", e);

				return { ok: false };
			}
		},
		[update, fetchList, listId]
	);

	return { isSaving, persist };
};
